export type ApprovalFallbackUser = {
  id?: number | null
  name?: string | null
  active?: boolean | null
  blocked?: boolean | null
  approval_limit?: number | null
  currency_approval_limit?: string | null
  priority_approval_limit?: number | null
  is_group?: boolean | null
  task_group_users?: any[] | null
}

export type ApprovalCreatorFallbackConfig = {
  add_creator_or_fallback?: boolean | null
  approval_fallback_user?: ApprovalFallbackUser | null
  approval_fallback_user_id?: number | null
}

export function isUsableApprovalUser(user?: ApprovalFallbackUser | null): boolean {
  const id = user?.id
  return id != null && Number.isFinite(Number(id)) && user?.active !== false && user?.blocked !== true
}

export function getPersistedApproverManuallyInserted(approver: {
  manually_inserted?: boolean | null
  creator_or_fallback_inserted?: boolean | null
  creator_or_fallback_removed?: boolean | null
}): boolean | undefined {
  return approver.creator_or_fallback_inserted || approver.creator_or_fallback_removed ? false : approver.manually_inserted ?? undefined
}

export function resolveCreatorOrFallback(
    config: ApprovalCreatorFallbackConfig | null | undefined,
    creator: ApprovalFallbackUser | null | undefined,
    context: 'procurement' | 'invoice',
    preferFallback: boolean = false
): ApprovalFallbackUser | null {
  if (!config?.add_creator_or_fallback) return null

  const shouldUseCreator = isUsableApprovalUser(creator)
      && (context === 'invoice' || !preferFallback)
  if (shouldUseCreator) return creator ?? null

  if (isUsableApprovalUser(config.approval_fallback_user)) return config.approval_fallback_user ?? null
  return null
}

export function addCreatorOrFallbackToApprovers<T extends {
  lb_user_id?: number | null
  lb_user?: any
  manually_inserted?: boolean
  creator_or_fallback_inserted?: boolean
  required?: boolean
}>(
    approvers: T[] | null | undefined,
    candidate: ApprovalFallbackUser | null | undefined
): Array<T & {
  manually_inserted?: boolean
  creator_or_fallback_inserted?: boolean
  required?: boolean
}> {
  if (!isUsableApprovalUser(candidate)) return [...(approvers ?? [])]

  const candidateId = Number(candidate?.id)
  const existingCandidate = (approvers ?? []).find((approver) => Number(approver?.lb_user_id) === candidateId)
  const existing = (approvers ?? []).filter((approver) => Number(approver?.lb_user_id) !== candidateId)
  const existingIsManual = existingCandidate?.manually_inserted === true && !existingCandidate?.creator_or_fallback_inserted
  const generated = {
    ...(existingCandidate ?? {}),
    lb_user_id: candidateId,
    lb_user: existingCandidate?.lb_user ?? candidate,
    manually_inserted: existingIsManual,
    creator_or_fallback_inserted: !existingIsManual,
    required: existingCandidate?.required ?? false,
  } as T & {
    manually_inserted?: boolean
    creator_or_fallback_inserted?: boolean
    required?: boolean
  }

  return [generated, ...existing]
}

export function prependCreatorOrFallbackApprovers<T extends {lb_user_id?: number | null}>(
    approvers: T[] | null | undefined,
    candidates: Array<T | null | undefined>
): T[] {
  const list = [...(approvers ?? [])]
  const first: T[] = []
  const seen = new Set<number>()

  candidates.forEach((candidate) => {
    const id = Number(candidate?.lb_user_id)
    if (!Number.isFinite(id) || seen.has(id)) return
    const index = list.findIndex((approver) => Number(approver?.lb_user_id) === id)
    if (index < 0) return
    seen.add(id)
    first.push(list[index])
    list.splice(index, 1)
  })

  return first.concat(list)
}
